/**
 * NotificationBell — Phase 63
 *
 * Universal in-app notification bell for the top Navbar.
 *
 * Features:
 *  • Red badge showing unread count (live via Supabase Realtime).
 *  • Click bell → popover (desktop) / bottom-sheet (mobile).
 *  • Click a notification → mark it read + navigate to action_url if present.
 *  • "Mark all read" button clears every unread row in one DB call.
 *  • Read/unread visual distinction: blue dot vs grey, bold vs normal weight.
 *  • Keyboard: Escape closes the panel.
 *  • Click-outside closes the panel.
 *  • Skeleton loading state on first fetch.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, CheckCheck, Inbox, ExternalLink } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { useNotifications } from '@/hooks/useNotifications';
import type { Notification } from '@/types/database';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  const d   = new Date(iso);
  const now = new Date();
  const diffMs  = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffH   = Math.floor(diffMs / 3_600_000);
  const diffD   = Math.floor(diffMs / 86_400_000);

  if (diffMin <  1)  return 'עכשיו';
  if (diffMin <  60) return `לפני ${diffMin} דק׳`;
  if (diffH   <  24) return `לפני ${diffH} שע׳`;
  if (diffD   <   7) return `לפני ${diffD} ימים`;

  return d.toLocaleDateString('he-IL', {
    day: '2-digit', month: '2-digit', year: '2-digit',
  });
}

// ── Single notification row ───────────────────────────────────────────────────

function NotificationRow({
  notification,
  onRead,
}: {
  notification: Notification;
  onRead:       (n: Notification) => void;
}) {
  const isUnread = !notification.is_read;

  return (
    <li>
      <button
        onClick={() => onRead(notification)}
        className={`w-full text-right flex items-start gap-3 px-4 py-3
                    hover:bg-gray-50 active:bg-gray-100 transition-colors
                    ${isUnread ? 'bg-blue-50/40' : ''}`}
        dir="rtl"
      >
        {/* Unread / read indicator dot */}
        <span
          className={`mt-1.5 w-2 h-2 rounded-full shrink-0 transition-colors
                      ${isUnread ? 'bg-primary-500' : 'bg-gray-200'}`}
          aria-hidden="true"
        />

        <div className="flex-1 min-w-0 text-right">
          {/* Title */}
          <p className={`text-sm truncate leading-snug
                         ${isUnread ? 'font-semibold text-gray-900' : 'font-normal text-gray-700'}`}>
            {notification.title}
          </p>

          {/* Message */}
          {notification.message && (
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
              {notification.message}
            </p>
          )}

          {/* Footer: time + action link hint */}
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[11px] text-gray-400">
              {formatTime(notification.created_at)}
            </span>
            {notification.action_url && (
              <span className="flex items-center gap-0.5 text-[11px] text-primary-500 font-medium">
                <ExternalLink className="w-2.5 h-2.5" />
                פתח
              </span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

// ── Skeleton rows shown while loading ─────────────────────────────────────────

function SkeletonRows() {
  return (
    <div className="p-4 space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-start gap-3 animate-pulse">
          <div className="mt-1.5 w-2 h-2 rounded-full bg-gray-200 shrink-0" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-gray-200 rounded w-3/4" />
            <div className="h-2.5 bg-gray-100 rounded w-full" />
            <div className="h-2 bg-gray-100 rounded w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function NotificationBell() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const [open, setOpen] = useState(false);
  const panelRef   = useRef<HTMLDivElement>(null);
  const buttonRef  = useRef<HTMLButtonElement>(null);

  const {
    notifications,
    unreadCount,
    loading,
    markOneRead,
    markAllRead,
  } = useNotifications(user?.id);

  // ── Close on click-outside ─────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current  && !panelRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Close on Escape ────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // ── Handle notification click ──────────────────────────────────────────
  //
  // 1. Mark as read (optimistic — instant badge update).
  // 2. Close the panel.
  // 3. Navigate to action_url if present.

  const handleNotificationClick = useCallback(async (n: Notification) => {
    if (!n.is_read) {
      await markOneRead(n.id);
    }
    setOpen(false);
    if (n.action_url) {
      navigate(n.action_url);
    }
  }, [markOneRead, navigate]);

  const handleMarkAllRead = useCallback(async () => {
    await markAllRead();
  }, [markAllRead]);

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="relative">

      {/* ── Bell button ────────────────────────────────────────────────── */}
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 text-gray-600 hover:bg-gray-100 active:bg-gray-200
                   rounded-lg transition-colors min-w-[40px] min-h-[40px]
                   flex items-center justify-center"
        aria-label={`התראות${unreadCount > 0 ? ` (${unreadCount} חדשות)` : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Bell className="w-5 h-5" />

        {/* Unread badge */}
        {unreadCount > 0 && (
          <span
            className="absolute top-1 right-1 min-w-[16px] h-4 px-0.5
                       flex items-center justify-center
                       bg-red-500 text-white text-[10px] font-bold
                       rounded-full leading-none pointer-events-none"
            aria-hidden="true"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* ── Panel ──────────────────────────────────────────────────────── */}
      {open && (
        <>
          {/* Mobile backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-40 sm:hidden"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          {/* Panel itself */}
          <div
            ref={panelRef}
            role="dialog"
            aria-label="התראות"
            dir="rtl"
            className={[
              'z-50 bg-white shadow-xl border border-gray-200 flex flex-col',
              // Mobile: full-width bottom-sheet
              'fixed bottom-0 left-0 right-0 rounded-t-2xl max-h-[75vh]',
              // Desktop: anchored popover
              'sm:absolute sm:bottom-auto sm:left-auto sm:right-0 sm:top-full sm:mt-2',
              'sm:w-96 sm:rounded-2xl sm:max-h-[500px]',
            ].join(' ')}
          >

            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-4 pb-3
                            border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-gray-500" />
                <h2 className="text-sm font-bold text-gray-900">התראות</h2>
                {unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-red-100 text-red-600
                                   text-xs font-semibold rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    title="סמן הכל כנקרא"
                    className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500
                               hover:text-primary-600 hover:bg-primary-50
                               rounded-lg transition-colors"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">סמן הכל כנקרא</span>
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600
                             hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="סגור"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <SkeletonRows />
              ) : notifications.length === 0 ? (
                /* Empty state */
                <div className="flex flex-col items-center justify-center
                                gap-3 py-14 px-4 text-center">
                  <div className="w-12 h-12 rounded-2xl bg-gray-100
                                  flex items-center justify-center">
                    <Inbox className="w-6 h-6 text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-400 font-medium">אין התראות</p>
                  <p className="text-xs text-gray-300">
                    התראות חדשות יופיעו כאן
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {notifications.map((n) => (
                    <NotificationRow
                      key={n.id}
                      notification={n}
                      onRead={handleNotificationClick}
                    />
                  ))}
                </ul>
              )}
            </div>

            {/* Mobile safe-area footer spacer */}
            <div className="shrink-0 pb-safe sm:pb-0" />
          </div>
        </>
      )}
    </div>
  );
}
